// Bundle provenance capture for FRONTEND-996/997.
//
// Those two issues are one failure across two JS engines: V8 says "Getter must
// be a function: <name>", SpiderMonkey says "property descriptor's get field is
// neither undefined nor a function". Both throw inside __webpack_require__.d
// while a lazily-loaded chunk evaluates, i.e. a module's export definition
// carried a non-function getter — which webpack never emits from our source.
//
// The 2026-07-24 sweep killed the obvious explanations: not one bad build (7+
// releases), not engine-specific (Opera/Chrome/Edge/Safari/Samsung/Norton), not
// a deploy race (median 11h after the last deploy), and not a [chunkhash]
// filename collision (a two-build diff found zero). What survived is that 100%
// of erroring pages ran an already-superseded bundle.
//
// So the one question left is whether a single page load pulled assets from
// more than one build. Capture what the page actually fetched and let the next
// sweep answer it from data instead of inference.

import _ from 'lodash';

// Keep the payload small — Sentry truncates large extras, and the decisive
// signal is mainBundles, not an exhaustive chunk inventory.
const MAX_CHUNKS_REPORTED = 40;

const FRONTEND_ASSET = /\/frontend\/([^/?#]+\.js)(?:[?#]|$)/;
const MAIN_BUNDLE = /^simbot\./;

// True for the FRONTEND-996/997 family. Both phrasings are distinctive enough
// to claim; neither appears in unrelated failures.
export const isChunkEvalFamily = (message) => {
  if (typeof message !== 'string') {
    return false;
  }
  return message.includes('Getter must be a function')
    || message.includes("property descriptor's get field");
};

// Pure: given the asset URLs a page loaded, report which builds they came from.
// `mixedBuilds` is the payload — more than one main bundle on a single page
// means that page was running two builds at once.
export const summarizeBundleAssets = (urls) => {
  const filenames = _.uniq(
    _.compact(_.map(urls, (url) => {
      if (typeof url !== 'string') {
        return null;
      }
      const match = url.match(FRONTEND_ASSET);
      return match ? match[1] : null;
    })),
  );

  const [mainBundles, chunks] = _.partition(filenames, name => MAIN_BUNDLE.test(name));

  return {
    mainBundles,
    mixedBuilds: mainBundles.length > 1,
    chunks: _.take(chunks, MAX_CHUNKS_REPORTED),
    chunkCount: chunks.length,
  };
};

// Thin shim over the browser globals. Deliberately untested — all the logic
// worth asserting on lives in summarizeBundleAssets. Never throws: this runs
// inside beforeSend, where an exception would drop the event entirely.
export const collectBundleProvenance = () => {
  try {
    const scriptSrcs = _.map(
      document.querySelectorAll('script[src]'),
      script => script.src,
    );
    // Script tags miss lazily-loaded chunks that were injected and removed, so
    // the resource timeline is the more complete source of the two.
    const resourceNames = _.map(
      performance.getEntriesByType('resource'),
      entry => entry.name,
    );
    return summarizeBundleAssets([...scriptSrcs, ...resourceNames]);
  } catch (err) {
    // Keep the shape stable so callers can tag mixedBuilds unconditionally.
    return {
      mainBundles: [],
      mixedBuilds: null,
      chunks: [],
      chunkCount: 0,
      bundleProvenanceError: String(err?.message || err),
    };
  }
};
